/**
 * Journey: Egg Donation, Female
 *
 * Steps: Learn > Apply/Screen > Match > Treatment
 */

import type { JourneyDefinition, PlanStep } from "../plan.types";

export const EGG_DONATION_GUIDE_ARTICLE_ID = "moon-article-egg-donation-guide";

const learnStep: PlanStep = {
  id: "learn",
  title: "Becoming an Egg Donor",
  subtitle: "Help someone become a parent",
  icon: "BookOpen",
  education: {
    context:
      "Egg donation helps individuals and couples who can't use their own eggs to have a child. As a donor, you'll go through ovarian stimulation and egg retrieval — the same process as egg freezing — but the eggs go to a recipient.",
    details:
      "Requirements differ by program and location. You may be asked about your age, health and family history, genetic or infectious-disease screening, tobacco or drug use, and willingness to complete medical and psychological screening.\n\nCompensation and covered expenses vary — ask for written terms before consenting. Plan for several weeks of screening, monitoring, retrieval, and recovery.",
    learnMoreLink: EGG_DONATION_GUIDE_ARTICLE_ID,
  },
  actions: [
    {
      label: "I understand this",
      variant: "primary",
      icon: "Check",
      signal: "complete-step",
    },
    {
      label: "Full donor guide",
      variant: "outline",
      icon: "BookOpen",
      screen: "ArticleDetail",
      params: { articleId: EGG_DONATION_GUIDE_ARTICLE_ID },
    },
  ],
  completionSignal: { type: "manual" },
};

const screenStep: PlanStep = {
  id: "screening",
  title: "Screening Process",
  subtitle: "Medical, psychological, and genetic screening",
  icon: "ClipboardCheck",
  education: {
    context:
      "Donor screening is comprehensive — clinics and agencies need to ensure your health and safety. This process typically takes 4-8 weeks. Everything is covered by the recipient or agency — no cost to you.",
    details:
      "Expect:\n• Medical screening: Blood work, genetic testing, infectious disease panel, ultrasound, physical exam\n• Psychological screening: Meeting with a mental health professional to discuss motivations and ensure informed consent\n• Genetic screening: Expanded carrier screening (100+ conditions) to assess risks\n• Background: Family medical history review",
  },
  actions: [
    {
      label: "Upload screening documents",
      variant: "primary",
      screen: "DocumentsScreen",
    },
  ],
  substeps: [
    {
      id: "application",
      title: "Complete application",
      education: "Your agency or clinic will provide an application form.",
      status: "not-started",
    },
    {
      id: "medical-screening",
      title: "Medical screening appointment",
      education: "Comprehensive physical exam, blood work, and ultrasound.",
      status: "not-started",
    },
    {
      id: "psychological-eval",
      title: "Psychological evaluation",
      education:
        "Meeting with a counselor to discuss motivations and expectations.",
      status: "not-started",
    },
    {
      id: "genetic-testing",
      title: "Genetic testing",
      education: "Expanded carrier screening for 100+ conditions.",
      status: "not-started",
    },
    {
      id: "background-review",
      title: "Background / family history review",
      education: "Detailed family medical history documentation.",
      status: "not-started",
    },
    {
      id: "screening-approved",
      title: "Screening approved",
      education:
        "Once all screening is complete and approved, you move to matching.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "all-substeps" },
};

const matchStep: PlanStep = {
  id: "match",
  title: "Getting Matched",
  subtitle: "Connect with a recipient",
  icon: "Heart",
  education: {
    context:
      "Once approved, your profile becomes available to recipients. Matching can happen through an agency, your clinic's internal program, or a known donation arrangement. Timeline varies widely — from days to months.",
  },
  actions: [],
  substeps: [
    {
      id: "profile-listed",
      title: "Profile approved and listed",
      education: "Your profile is now visible to potential recipients.",
      status: "not-started",
    },
    {
      id: "matched",
      title: "Matched with recipient",
      education: "A recipient has selected you as their donor.",
      status: "not-started",
    },
    {
      id: "legal-agreement",
      title: "Legal agreement signed",
      education:
        "Both parties sign legal documents covering rights, compensation, and terms.",
      status: "not-started",
    },
    {
      id: "cycle-scheduled",
      title: "Cycle coordination scheduled",
      education:
        "Your clinic coordinates timing between you and the recipient.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "all-substeps" },
};

const treatmentStep: PlanStep = {
  id: "treatment",
  title: "Your Donation Cycle",
  subtitle: "Stimulation + egg retrieval",
  icon: "Syringe",
  education: {
    context:
      "Your donation cycle is active. Track daily medications, appointments, and symptoms in the Care section.",
    details:
      "Same stimulation + retrieval process as egg freezing, but all costs are covered by the recipient.\n\nPhases: baseline → stimulation (10-14 days) → trigger → retrieval (~20 min under light sedation) → complete.\n\nRecovery: 1-2 days — most return to normal activities quickly.\n\nYour protocol may be coordinated with the recipient's cycle if a fresh transfer is planned. Your clinic handles this timing.\n\nYour Care page has your daily medication schedule, symptom tracker, and appointment calendar.",
  },
  actions: [
    {
      label: "Go to Care",
      variant: "primary",
      icon: "Heart",
      screen: "CareOverview",
    },
    { label: "Set your start date", variant: "outline", icon: "Calendar" },
  ],
  completionSignal: { type: "external" },
};

export const EGG_DONATION: JourneyDefinition = {
  key: "egg-donation",
  gender: "female",
  goal: "egg-donation",
  pathway: "solo",
  title: "Egg Donation",
  description: "Help someone become a parent",
  steps: [learnStep, screenStep, matchStep, treatmentStep],
};
